import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface BlockedUser {
  id: string;
  blocked_id: string;
  created_at: string;
  profile?: {
    id: string;
    username: string;
    name: string;
    profile_picture?: string | null;
  } | null;
}

/**
 * Ids of users the signed-in user has blocked.
 * Shared across surfaces so feeds/comments/search can filter consistently.
 */
export const useBlockedUserIds = () => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['blocked-users', 'ids', user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('blocked_users')
        .select('blocked_id')
        .eq('blocker_id', user!.id);
      if (error) throw error;
      return (data || []).map((r: any) => r.blocked_id as string);
    },
  });

  const blockedIds = useMemo(
    () => new Set<string>(query.data || []),
    [query.data]
  );

  const isBlocked = useCallback(
    (userId?: string | null) => !!userId && blockedIds.has(userId),
    [blockedIds]
  );

  return { blockedIds, isBlocked, isLoading: query.isLoading };
};

/** Full block list with profile info — used by the Settings screen. */
export const useBlockedUsers = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['blocked-users', 'list', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<BlockedUser[]> => {
      const { data, error } = await supabase
        .from('blocked_users')
        .select('id, blocked_id, created_at')
        .eq('blocker_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data || []) as any[];
      if (rows.length === 0) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, name, profile_picture')
        .in('id', rows.map((r) => r.blocked_id));
      const pmap = new Map((profiles || []).map((p: any) => [p.id, p]));

      return rows.map((r) => ({
        id: r.id,
        blocked_id: r.blocked_id,
        created_at: r.created_at,
        profile: pmap.get(r.blocked_id) || null,
      }));
    },
  });
};

const invalidateBlockCaches = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['blocked-users'] });
  qc.invalidateQueries({ queryKey: ['reviews'] });
  qc.invalidateQueries({ queryKey: ['crowd-feed'] });
  qc.invalidateQueries({ queryKey: ['comments'] });
  qc.invalidateQueries({ queryKey: ['list-comments'] });
  qc.invalidateQueries({ queryKey: ['friends-activities'] });
  qc.invalidateQueries({ queryKey: ['notifications'] });
  qc.invalidateQueries({ queryKey: ['follow-relationship'] });
};

export const useBlockUser = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (blockedId: string) => {
      if (!user?.id) throw new Error('You need to be signed in.');
      if (user.id === blockedId) throw new Error('You cannot block yourself.');

      const { error } = await supabase
        .from('blocked_users')
        .insert({ blocker_id: user.id, blocked_id: blockedId });
      // 23505 = already blocked; treat as success
      if (error && (error as any).code !== '23505') throw error;

      // Break any existing follow relationship in both directions.
      await supabase
        .from('followers')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', blockedId);
      await supabase
        .from('followers')
        .delete()
        .eq('follower_id', blockedId)
        .eq('following_id', user.id);

      return blockedId;
    },
    onSuccess: () => invalidateBlockCaches(queryClient),
  });
};

export const useUnblockUser = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (blockedId: string) => {
      if (!user?.id) throw new Error('You need to be signed in.');
      const { error } = await supabase
        .from('blocked_users')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', blockedId);
      if (error) throw error;
      return blockedId;
    },
    onSuccess: () => invalidateBlockCaches(queryClient),
  });
};
